import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import {
  ArrowLeft,
  ImagePlus,
  ImageOff,
  Loader2,
  Save,
  Trash2,
  MapPin,
} from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import CategoryCombobox from "@/components/CategoryCombobox";
import PickerMap from "@/components/PickerMap";
import { compressImage } from "@/lib/image";

function Field({ label, children }) {
  return (
    <div className="space-y-1.5">
      <label className="block text-[11px] font-bold uppercase tracking-[0.15em] text-slate-500">
        {label}
      </label>
      {children}
    </div>
  );
}

function CoordBox({ label, value, testid }) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white px-2.5 py-1.5">
      <p className="text-[9px] font-bold uppercase tracking-[0.1em] text-slate-400">
        {label}
      </p>
      <p className="coord-box truncate text-sm text-slate-800" data-testid={testid}>
        {value}
      </p>
    </div>
  );
}

export default function ItemForm({ mode }) {
  const { permid } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { categories, reload, items } = useData();
  const { authed } = useAuth();

  const defaultCategory =
    mode === "create" ? location.state?.defaultCategory || "" : "";

  const [title, setTitle] = useState("");
  const [category, setCategory] = useState(defaultCategory);
  const [address, setAddress] = useState("");
  const [threeWords, setThreeWords] = useState("");
  const [coords, setCoords] = useState(() => {
    if (mode === "create") {
      const wc = (items || []).filter((i) => i.latitude != null);
      if (wc.length) {
        const lat = wc.reduce((s, i) => s + i.latitude, 0) / wc.length;
        const lng = wc.reduce((s, i) => s + i.longitude, 0) / wc.length;
        return { lat: Number(lat.toFixed(6)), lng: Number(lng.toFixed(6)), alt: 0 };
      }
    }
    return { lat: null, lng: null, alt: 0 };
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [accuracy, setAccuracy] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(mode === "edit");
  const [w3wLoading, setW3wLoading] = useState(false);
  const [relocateSignal, setRelocateSignal] = useState(0);
  const w3wTimer = useRef(null);

  useEffect(() => {
    if (!authed) {
      navigate("/admin");
      return;
    }
    if (mode === "edit" && permid) {
      api
        .getObject(permid)
        .then(({ data }) => {
          setTitle(data.title || "");
          setCategory(data.category || "");
          setAddress(data.address || "");
          setThreeWords(data.threeWords || "");
          setCoords({
            lat: data.latitude,
            lng: data.longitude,
            alt: data.altitude || 0,
          });
          if (data.hasImage) setImagePreview(api.imageUrl(permid));
        })
        .catch(() => toast.error("Objekt konnte nicht geladen werden"))
        .finally(() => setLoading(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, permid]);

  const handleCoords = (lat, lng) => {
    setCoords((c) => ({ ...c, lat, lng }));
    if (w3wTimer.current) clearTimeout(w3wTimer.current);
    setW3wLoading(true);
    w3wTimer.current = setTimeout(async () => {
      try {
        const { data } = await api.w3w(lat, lng);
        if (data.words) setThreeWords(data.words);
      } catch (e) {
        /* ignore */
      } finally {
        setW3wLoading(false);
      }
    }, 700);
  };

  const handleGps = ({ accuracy: acc, altitude }) => {
    setAccuracy(acc != null ? Math.round(acc) : null);
    setCoords((c) => ({ ...c, alt: altitude != null ? Math.round(altitude) : c.alt }));
  };

  const onImage = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const compressed = await compressImage(f);
    setImageFile(compressed);
    setImagePreview(URL.createObjectURL(compressed));
  };

  const resetForNext = () => {
    setTitle("");
    setCategory(defaultCategory);
    setAddress("");
    setImageFile(null);
    setImagePreview(null);
    setAccuracy(null);
    // request a fresh GPS position for the next item (create loop)
    setRelocateSignal((n) => n + 1);
    const scroller = document.querySelector("[data-testid='form-scroll']");
    if (scroller) scroller.scrollTo({ top: 0, behavior: "smooth" });
    const el = document.querySelector("[data-testid='title-input']");
    if (el) el.focus();
  };

  const save = async () => {
    if (!title.trim()) return toast.error("Bitte einen Titel eingeben");
    if (coords.lat == null || coords.lng == null)
      return toast.error("Bitte die Position auf der Karte festlegen");

    setSaving(true);
    const fd = new FormData();
    fd.append("title", title.trim());
    fd.append("category", category || "");
    fd.append("address", address || "");
    fd.append("threeWords", threeWords || "");
    fd.append("latitude", String(coords.lat));
    fd.append("longitude", String(coords.lng));
    fd.append("altitude", String(coords.alt || 0));
    if (imageFile && mode === "create") fd.append("image", imageFile);

    try {
      if (mode === "edit") {
        await api.updateObject(permid, fd);
        toast.success("Gespeichert");
        await reload();
        navigate(`/item/${permid}`);
      } else {
        await api.createObject(fd);
        toast.success("Objekt angelegt – nächstes Objekt anlegen");
        await reload();
        resetForNext();
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Dieses Objekt wirklich löschen?")) return;
    try {
      await api.deleteObject(permid);
      toast.success("Gelöscht");
      await reload();
      navigate("/admin");
    } catch (e) {
      toast.error("Löschen fehlgeschlagen");
    }
  };

  if (loading) {
    return (
      <div className="grid h-[100dvh] w-full place-items-center bg-slate-50">
        <Loader2 className="animate-spin text-primary" size={28} />
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex h-[100dvh] w-full flex-col bg-slate-50">
      <header className="z-10 flex shrink-0 items-center gap-2 border-b border-slate-200 bg-white px-3 py-3">
        <button
          data-testid="back-button"
          onClick={() => navigate(-1)}
          className="grid h-10 w-10 place-items-center rounded-xl text-slate-600 transition-colors hover:bg-slate-100"
        >
          <ArrowLeft size={20} />
        </button>
        <h1 className="flex-1 truncate font-display text-lg font-bold text-slate-900">
          {mode === "edit" ? "Bearbeiten" : "Neues Objekt"}
        </h1>
      </header>

      <div className="flex-1 overflow-y-auto p-4 pb-28" data-testid="form-scroll">
        <div className="mx-auto max-w-md space-y-5">
          {/* Image */}
          {mode === "create" ? (
            <label
              className={`relative block w-full cursor-pointer overflow-hidden rounded-2xl border-2 border-dashed border-slate-300 bg-white ${
                imagePreview ? "h-44" : "h-20"
              }`}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Vorschau"
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full items-center justify-center gap-2 text-slate-400">
                  <ImagePlus size={20} />
                  <span className="text-sm font-medium">Bild hinzufügen</span>
                </div>
              )}
              <input
                type="file"
                accept="image/*"
                className="hidden"
                onChange={onImage}
                data-testid="image-input"
              />
            </label>
          ) : (
            <div
              className={`relative block w-full overflow-hidden rounded-2xl border border-slate-200 bg-white ${
                imagePreview ? "h-44" : "h-20"
              }`}
            >
              {imagePreview ? (
                <img
                  src={imagePreview}
                  alt="Vorschau"
                  className="h-full w-full object-cover"
                  data-testid="edit-image-preview"
                />
              ) : (
                <div className="flex h-full items-center justify-center gap-2 text-slate-400">
                  <ImageOff size={20} />
                  <span className="text-sm font-medium">Kein Bild vorhanden</span>
                </div>
              )}
            </div>
          )}

          <Field label="Titel">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="z.B. Haupteingang WC"
              className="h-12 rounded-xl"
              data-testid="title-input"
            />
          </Field>

          <Field label="Kategorie">
            <CategoryCombobox
              categories={categories}
              value={category}
              onChange={setCategory}
            />
          </Field>

          <Field label="Position (Karte verschieben)">
            <div className="mb-2 grid grid-cols-3 gap-2">
              <CoordBox
                label="Latitude"
                value={coords.lat != null ? coords.lat.toFixed(6) : "–"}
                testid="coord-lat"
              />
              <CoordBox
                label="Longitude"
                value={coords.lng != null ? coords.lng.toFixed(6) : "–"}
                testid="coord-lng"
              />
              <CoordBox
                label="Genauigkeit"
                value={accuracy != null ? `±${accuracy} m` : "–"}
                testid="gps-accuracy"
              />
            </div>
            <PickerMap
              lat={coords.lat}
              lng={coords.lng}
              onChange={handleCoords}
              onGps={handleGps}
              autoLocate={mode === "create"}
              relocateSignal={relocateSignal}
            />
          </Field>

          <Field label="what3words (automatisch)">
            <div className="flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-3">
              <MapPin size={16} className="shrink-0 text-red-500" />
              <span className="font-semibold text-red-500">///</span>
              <span className="flex-1 truncate text-slate-800" data-testid="three-words">
                {threeWords || (coords.lat != null ? "wird ermittelt…" : "Position wählen")}
              </span>
              {w3wLoading && <Loader2 size={15} className="animate-spin text-slate-400" />}
            </div>
          </Field>

          <Field label="Adresse">
            <Input
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="Straße, PLZ Ort"
              className="h-12 rounded-xl"
              data-testid="address-input"
            />
          </Field>

          {mode === "edit" && (
            <Button
              type="button"
              variant="outline"
              onClick={del}
              data-testid="delete-button"
              className="h-11 w-full rounded-xl border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <Trash2 size={16} className="mr-2" /> Objekt löschen
            </Button>
          )}
        </div>
      </div>

      <div className="absolute inset-x-0 bottom-0 z-20 border-t border-slate-200 bg-white p-4">
        <div className="mx-auto max-w-md">
          <Button
            onClick={save}
            disabled={saving}
            data-testid="save-button"
            className="h-12 w-full rounded-xl bg-primary text-white font-semibold hover:bg-primary/90"
          >
            {saving ? (
              <Loader2 className="mr-2 animate-spin" size={18} />
            ) : (
              <Save size={18} className="mr-2" />
            )}
            Speichern
          </Button>
        </div>
      </div>
    </div>
  );
}
